import { useState, useEffect, useCallback } from 'react';

/**
 * 🎯 Hook para persistencia completa del estado del laboratorio
 * Maneja: labProgress + formData + aiResponses + timestamps
 * 
 * @param {string} sessionId - ID de la sesión actual
 * @param {string} labId - ID del laboratorio (ej: 'lab1')
 * @returns {object} Funciones y estado para persistencia
 */
const useLabPersistence = (sessionId, labId) => {
  const [persistedData, setPersistedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Clave para localStorage
  const storageKey = `lab_${sessionId}_${labId}`;

  /**
   * 📥 Estructura completa de datos persistidos
   */
  const createEmptyLabState = () => ({
    labProgress: {
      contexto: true, // Siempre disponible
      hipotesis: { approved: false, score: null, timestamp: null },
      bml_build: { approved: false, score: null, timestamp: null },
      bml_measure: { approved: false, score: null, timestamp: null },
      bml_learn: { approved: false, score: null, timestamp: null },
      quiz: { approved: false, score: null, timestamp: null },
      resumen: { approved: false, score: null, timestamp: null }
    },
    formData: {
      hipotesis: '',
      bml_build: null,
      bml_measure: null,
      bml_learn: null,
      quiz: null
    },
    aiResponses: {
      hipotesis: null,
      bml_build: null,
      bml_measure: null,
      bml_learn: null,
      quiz: null
    },
    metadata: {
      created: Date.now(),
      lastUpdated: Date.now(),
      version: '1.0'
    }
  });

  /**
   * 📥 Cargar datos persistidos del localStorage
   */
  const loadPersistedData = useCallback(() => {
    try {
      setIsLoading(true);
      const savedData = localStorage.getItem(storageKey);
      
      if (savedData) {
        const parsed = JSON.parse(savedData);
        
        // Validar estructura y versión
        if (parsed.metadata && parsed.labProgress && parsed.formData) {
          console.log(`[📥 Lab Persistence] Datos cargados para ${labId}:`, parsed);
          setPersistedData(parsed);
          return parsed;
        } else {
          console.warn('[⚠️ Lab Persistence] Datos corruptos, creando estado limpio');
          const emptyState = createEmptyLabState();
          setPersistedData(emptyState);
          return emptyState;
        }
      } else {
        console.log('[📥 Lab Persistence] No hay datos previos, creando estado inicial');
        const emptyState = createEmptyLabState();
        setPersistedData(emptyState);
        return emptyState;
      }
    } catch (error) {
      console.error('[❌ Lab Persistence] Error cargando datos:', error);
      const emptyState = createEmptyLabState();
      setPersistedData(emptyState);
      return emptyState;
    } finally {
      setIsLoading(false);
    }
  }, [storageKey, labId]);

  /**
   * 💾 Guardar progreso de un step específico
   */
  const saveStepProgress = useCallback((stepId, { approved, score, formData, aiResponse }) => {
    try {
      const currentData = persistedData || createEmptyLabState();
      
      const updatedData = {
        ...currentData,
        labProgress: {
          ...currentData.labProgress,
          [stepId]: {
            approved: approved,
            score: score,
            timestamp: Date.now()
          }
        },
        formData: {
          ...currentData.formData,
          [stepId]: formData
        },
        aiResponses: {
          ...currentData.aiResponses,
          [stepId]: aiResponse
        },
        metadata: {
          ...currentData.metadata,
          lastUpdated: Date.now()
        }
      };

      // Guardar en localStorage
      localStorage.setItem(storageKey, JSON.stringify(updatedData));
      
      // Actualizar estado local
      setPersistedData(updatedData);
      
      console.log(`[💾 Lab Persistence] Guardado ${stepId}:`, {
        approved,
        score,
        hasFormData: !!formData,
        hasAIResponse: !!aiResponse
      });

      return updatedData;
    } catch (error) {
      console.error('[❌ Lab Persistence] Error guardando step:', error);
      return persistedData;
    }
  }, [persistedData, storageKey]);

  /**
   * 📊 Obtener estado de un step específico
   */
  const getStepState = useCallback((stepId) => {
    if (!persistedData) return null;
    
    return {
      progress: persistedData.labProgress[stepId] || { approved: false, score: null, timestamp: null },
      formData: persistedData.formData[stepId] || null,
      aiResponse: persistedData.aiResponses[stepId] || null
    };
  }, [persistedData]);

  /**
   * 🔍 Verificar si un step necesita re-evaluación IA
   */
  const shouldReEvaluate = useCallback((stepId, currentFormData) => {
    const stepState = getStepState(stepId);
    
    if (!stepState || !stepState.aiResponse || !stepState.formData) {
      return true; // No hay evaluación previa
    }

    // Comparar datos del formulario actual vs guardados
    try {
      const currentDataStr = JSON.stringify(currentFormData);
      const savedDataStr = JSON.stringify(stepState.formData);
      
      if (currentDataStr !== savedDataStr) {
        console.log(`[🔄 Lab Persistence] Datos modificados en ${stepId}, re-evaluación necesaria`);
        return true;
      }
      
      console.log(`[✅ Lab Persistence] Datos sin cambios en ${stepId}, usando evaluación existente`);
      return false;
    } catch (error) {
      console.error('[❌ Lab Persistence] Error comparando datos:', error);
      return true; // Por seguridad, re-evaluar
    }
  }, [getStepState]);

  /**
   * 🗑️ Limpiar datos del laboratorio
   */
  const clearLabData = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      const emptyState = createEmptyLabState();
      setPersistedData(emptyState);
      console.log(`[🗑️ Lab Persistence] Datos limpiados para ${labId}`);
      return emptyState;
    } catch (error) {
      console.error('[❌ Lab Persistence] Error limpiando datos:', error);
      return persistedData;
    }
  }, [storageKey, labId, persistedData]);

  /**
   * 🔄 Reiniciar específicamente el quiz 
   */
  const resetQuizStep = useCallback(() => {
    try {
      // 🛡️ Verificar que persistedData no sea null
      if (!persistedData) {
        console.warn('[⚠️ Lab Persistence] persistedData es null, inicializando datos por defecto');
        const emptyState = createEmptyLabState();
        setPersistedData(emptyState);
        return emptyState;
      }

      const updatedData = {
        ...persistedData,
        labProgress: {
          ...persistedData.labProgress,
          quiz: { approved: false, score: null, timestamp: null }
        },
        formData: {
          ...persistedData.formData,
          quiz: null
        },
        aiResponses: {
          ...persistedData.aiResponses,
          quiz: null
        }
      };

      localStorage.setItem(storageKey, JSON.stringify(updatedData));
      setPersistedData(updatedData);
      
      console.log(`[🔄 Lab Persistence] Quiz reiniciado para ${labId}`);
      return updatedData;
    } catch (error) {
      console.error('[❌ Lab Persistence] Error reiniciando quiz:', error);
      return persistedData;
    }
  }, [storageKey, labId, persistedData]);

  /**
   * 📈 Calcular progreso general del lab
   */
  const getLabProgressPercentage = useCallback(() => {
    if (!persistedData) return 0;
    
    // ✅ Usar los pasos principales del laboratorio InnovaTech (mismos nombres que se guardan)
    const steps = ['hipotesis', 'bml_build', 'bml_measure', 'bml_learn', 'quiz', 'resumen'];
    
    // Contar contexto como siempre completado + pasos aprobados
    const contextCompleted = 1; // Contexto siempre está completado
    const completedSteps = contextCompleted + steps.filter(step => 
      persistedData.labProgress[step]?.approved === true
    ).length;
    
    const totalSteps = steps.length + 1; // +1 para contexto
    
    console.log('[📊 PROGRESS] Pasos completados:', completedSteps, 'de', totalSteps);
    console.log('[📊 PROGRESS] Estado actual:', Object.keys(persistedData.labProgress).filter(key => persistedData.labProgress[key]?.approved));
    
    return Math.round((completedSteps / totalSteps) * 100);
  }, [persistedData]);

  /**
   * 🔍 Verificar si puede avanzar a un step
   */
  const canAdvanceToStep = useCallback((targetStepId) => {
    if (!persistedData) return false;
    
    const stepOrder = ['contexto', 'hipotesis', 'bml_build', 'bml_measure', 'bml_simulate', 'bml_learn', 'quiz', 'resumen'];
    const targetIndex = stepOrder.indexOf(targetStepId);
    
    if (targetIndex <= 0) return true; // contexto siempre disponible
    
    // Verificar que todos los steps previos estén aprobados
    for (let i = 1; i < targetIndex; i++) {
      const stepId = stepOrder[i];
      if (!persistedData.labProgress[stepId]?.approved) {
        return false;
      }
    }
    
    return true;
  }, [persistedData]);

  // Cargar datos al montar el hook
  useEffect(() => {
    if (sessionId && labId) {
      loadPersistedData();
    }
  }, [sessionId, labId, loadPersistedData]);

  return {
    // Estado
    persistedData,
    isLoading,
    
    // Funciones principales
    saveStepProgress,
    getStepState,
    shouldReEvaluate,
    clearLabData,
    resetQuizStep,
    loadPersistedData,
    
    // Utilidades
    getLabProgressPercentage,
    canAdvanceToStep,
    
    // Metadata
    storageKey
  };
};

export default useLabPersistence;